import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone, PhoneCall, Clock, MapPin, Building2, Hammer, MessageSquare, ShieldCheck,
  Home as HomeIcon, Wrench, DoorOpen, Check, ArrowRight, AlertTriangle, Star,
  ChevronDown, Send, Info,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import {
  BUSINESS, CATEGORIES, WHY_US, STEPS, GALLERY, FAQS, SERVICE_AREAS, LEAD_SERVICES,
} from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHY_ICONS = { Clock, MapPin, Building2, Hammer, MessageSquare, ShieldCheck };
const CAT_ICONS = { residential: HomeIcon, commercial: Building2, repair: Wrench, installation: DoorOpen };

const Eyebrow = ({ children }) => (
  <p className="mb-2 text-xs font-bold tracking-widest" style={{ color: "var(--brand-blue)" }}>{children}</p>
);

const SectionTitle = ({ eyebrow, title, subtitle }) => (
  <div className="mb-8">
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{title}</h2>
    {subtitle && <p className="mt-2 max-w-2xl text-slate-500">{subtitle}</p>}
  </div>
);

function RequestForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", message: "" });
  const [service, setService] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, service });
      toast.success("Request sent! We'll get back to you shortly.");
      setForm({ name: "", phone: "", email: "", city: "", message: "" });
      setService("");
    } catch (e) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-slate-900 outline-none transition-colors focus:border-blue-500 focus:bg-white";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6" data-testid="home-quote-form">
      <div className="mb-4">
        <p className="mb-1.5 text-sm font-semibold text-slate-700">What do you need? (optional)</p>
        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
          {LEAD_SERVICES.map((s) => {
            const active = service === s;
            return (
              <button
                key={s}
                type="button"
                data-testid={`lead-service-${s}`}
                onClick={() => setService(active ? "" : s)}
                className="whitespace-nowrap rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors"
                style={
                  active
                    ? { backgroundColor: "var(--brand-blue)", borderColor: "var(--brand-blue)", color: "#fff" }
                    : { backgroundColor: "#f8fafc", borderColor: "#e2e8f0", color: "#334155" }
                }
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3.5">
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-slate-700">Your name</label>
          <input data-testid="lead-name" className={inputCls} placeholder="e.g. Jordan" value={form.name} onChange={update("name")} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-slate-700">Phone number</label>
          <input data-testid="lead-phone" type="tel" className={inputCls} placeholder="(306) 000-0000" value={form.phone} onChange={update("phone")} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-slate-700">Email (optional)</label>
          <input data-testid="lead-email" type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={update("email")} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-slate-700">City / town (optional)</label>
          <input data-testid="lead-city" className={inputCls} placeholder="e.g. Regina" value={form.city} onChange={update("city")} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-slate-700">Message (optional)</label>
          <textarea data-testid="lead-issue" rows={4} className={`${inputCls} resize-none`} placeholder="e.g. Loud bang, door won't open" value={form.message} onChange={update("message")} />
        </div>
        <button
          data-testid="lead-submit"
          onClick={submit}
          disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-base font-semibold text-white transition-transform hover:scale-[1.01] disabled:opacity-60"
          style={{ backgroundColor: "var(--brand-blue)" }}
        >
          {loading ? "Sending..." : "Request Service"} <Send size={18} />
        </button>
      </div>
    </div>
  );
}

function Faq({ item, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3.5" data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 text-left"
        data-testid={`${testid}-toggle`}
      >
        <span className="font-semibold text-slate-900">{item.q}</span>
        <ChevronDown size={20} style={{ color: "var(--brand-blue)" }} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="mt-3 text-[15px] leading-relaxed text-slate-500">{item.a}</p>}
    </div>
  );
}

export default function Home() {
  return (
    <div id="top" className="min-h-screen" style={{ backgroundColor: "#f8fafc" }}>
      <Navbar />

      {/* HERO */}
      <section className="pt-[68px]" style={{ backgroundColor: "var(--navy)" }}>
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
          <div>
            <div className="mb-5 flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "var(--gold)" }} />
              <span className="text-xs font-bold tracking-widest" style={{ color: "var(--gold)" }}>
                REGINA, SASKATCHEWAN · AVAILABLE 24/7
              </span>
            </div>
            <h1 data-testid="hero-title" className="text-4xl font-extrabold leading-tight text-white sm:text-5xl">
              Regina's Garage Door Experts — Available 24/7
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-300">
              From broken springs and damaged cables to complete garage door installations, Regina Garage
              Door Services provides professional residential and commercial garage door solutions when you need them.
            </p>
            <a href={BUSINESS.phoneHref} data-testid="hero-phone" className="mt-6 flex items-center gap-3">
              <Phone size={28} style={{ color: "var(--gold)" }} />
              <span className="text-2xl font-bold text-white">{BUSINESS.phone}</span>
            </a>
            <div className="mt-6 flex max-w-md flex-col gap-3 sm:flex-row">
              <a
                href={BUSINESS.phoneHref}
                data-testid="hero-call-button"
                className="flex flex-1 items-center justify-center gap-2 rounded-xl py-3.5 text-lg font-semibold text-white transition-transform hover:scale-[1.02]"
                style={{ backgroundColor: "var(--brand-blue)" }}
              >
                <PhoneCall size={20} /> CALL NOW
              </a>
              <a
                href="#request"
                data-testid="hero-request-button"
                className="flex flex-1 items-center justify-center rounded-xl border py-3.5 font-semibold text-white transition-colors hover:bg-white/10"
                style={{ borderColor: "rgba(255,255,255,0.3)" }}
              >
                REQUEST SERVICE
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
              {["24/7 Service", "Residential & Commercial", "Garage Door Repair & Installation", "Local Regina Service", "Professional Workmanship"].map((t) => (
                <span key={t} className="flex items-center gap-1.5 text-sm font-medium text-slate-200">
                  <Check size={15} style={{ color: "var(--gold)" }} /> {t}
                </span>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="relative h-72 overflow-hidden rounded-3xl sm:h-full">
              <img src="/assets/jobs/door-walnut.jpg" alt="Walnut wood-look insulated garage door" className="h-full w-full object-cover" />
            </div>
            <div className="absolute bottom-4 left-4 rounded-lg bg-black/60 px-3 py-2 backdrop-blur">
              <span className="text-xs font-semibold text-white">Walnut wood-look insulated door with window row</span>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
        {/* EMERGENCY BAND */}
        <div
          className="mb-14 flex flex-col items-start gap-4 rounded-2xl border border-l-4 bg-white p-5 sm:flex-row sm:items-center"
          style={{ borderColor: "#e2e8f0", borderLeftColor: "var(--gold)" }}
          data-testid="emergency-band"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: "#fef3c7" }}>
            <AlertTriangle size={22} style={{ color: "#d97706" }} />
          </span>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-slate-900">Garage Door Problem? We're Ready to Help.</h3>
            <p className="mt-1 text-[15px] leading-relaxed text-slate-500">
              A broken spring, damaged cable, door that won't open, or a malfunctioning opener can leave you stuck.
              Our team provides 24/7 garage door service in Regina and surrounding areas to help get your door working safely again.
            </p>
          </div>
          <a
            href={BUSINESS.phoneHref}
            data-testid="emergency-call"
            className="flex shrink-0 items-center gap-2 rounded-xl px-5 py-3 font-semibold text-white"
            style={{ backgroundColor: "var(--brand-blue)" }}
          >
            <Phone size={18} /> Call {BUSINESS.phone}
          </a>
        </div>

        {/* SERVICES */}
        <section id="services" className="mb-16">
          <SectionTitle eyebrow="OUR SERVICES" title="Residential and commercial garage door services in Regina" />
          <div className="grid gap-5 md:grid-cols-2">
            {CATEGORIES.map((cat) => {
              const Icon = CAT_ICONS[cat.id];
              return (
                <div key={cat.id} className="rounded-2xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-md" data-testid={`cat-${cat.id}`}>
                  <div className="flex items-start gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: "var(--brand-blue-light)" }}>
                      <Icon size={22} style={{ color: "var(--brand-blue)" }} />
                    </span>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{cat.title}</h3>
                      <p className="mt-1 text-sm text-slate-500">{cat.subtitle}</p>
                    </div>
                  </div>
                  <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {cat.items.map((it) => (
                      <span key={it} className="flex items-center gap-2 text-[15px] text-slate-700">
                        <Check size={15} style={{ color: "var(--green)" }} className="shrink-0" /> {it}
                      </span>
                    ))}
                  </div>
                  <a href="#request" className="mt-5 flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--brand-blue)" }}>
                    {cat.linkLabel} <ArrowRight size={16} />
                  </a>
                </div>
              );
            })}
          </div>
        </section>

        {/* WHY US */}
        <section className="mb-16" data-testid="why-us-section">
          <SectionTitle eyebrow="WHY CHOOSE US" title="Why Regina Homeowners and Businesses Choose Us" />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_US.map((w) => {
              const Icon = WHY_ICONS[w.icon];
              return (
                <div key={w.title} className="rounded-2xl border border-slate-200 bg-white p-6">
                  <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border" style={{ backgroundColor: "#0f172a", borderColor: "#0f172a" }}>
                    <Icon size={20} style={{ color: "var(--gold)" }} />
                  </span>
                  <h3 className="font-bold text-slate-900">{w.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-slate-500">{w.text}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-5 flex items-start gap-4 rounded-2xl border p-6" style={{ backgroundColor: "var(--brand-blue-light)", borderColor: "#bfdbfe" }} data-testid="different-note">
            <Info size={22} style={{ color: "var(--brand-blue)" }} className="shrink-0" />
            <div>
              <h3 className="font-bold" style={{ color: "var(--brand-blue-dark)" }}>Every Garage Door Is Different</h3>
              <p className="mt-1 text-[15px] leading-relaxed text-slate-700">
                The condition of the door, type of repair, parts required, door size, and other factors can affect the work involved.
                We provide an assessment on-site and discuss the recommended options with you before proceeding.
              </p>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="mb-16" data-testid="how-it-works">
          <SectionTitle eyebrow="HOW IT WORKS" title="Simple from the first call" />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-2xl border border-slate-200 bg-white p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-full text-lg font-bold text-white" style={{ backgroundColor: "var(--brand-blue)" }}>
                  {s.n}
                </span>
                <h3 className="mt-4 font-bold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-500">{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ABOUT */}
        <section id="about" className="mb-16">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <img src="/assets/jobs/door-white.jpg" alt="Classic raised-panel steel garage door" className="h-64 w-full object-cover lg:h-full" />
            </div>
            <div className="flex flex-col justify-center">
              <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Garage Door Services for Regina Homes</h2>
              <p className="mt-3 leading-relaxed text-slate-500">
                Your garage door is one of the most frequently used parts of your home. When it isn't working properly,
                it can quickly become frustrating and inconvenient. Regina Garage Door Services provides repair, maintenance,
                replacement, and installation services for residential garage doors.
              </p>
              <a href={BUSINESS.phoneHref} className="mt-5 flex w-fit items-center gap-2 rounded-xl px-5 py-3 font-semibold text-white" style={{ backgroundColor: "var(--brand-blue)" }}>
                <Phone size={18} /> Call for Residential Service
              </a>
            </div>
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div className="order-2 flex flex-col justify-center lg:order-1">
              <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Commercial Garage Door Services</h2>
              <p className="mt-3 leading-relaxed text-slate-500">
                A reliable overhead door is important for keeping your business moving. We provide garage door repair,
                installation, replacement, and maintenance services for commercial properties in Regina and surrounding areas.
              </p>
              <a href={BUSINESS.phoneHref} className="mt-5 flex w-fit items-center gap-2 rounded-xl px-5 py-3 font-semibold text-white" style={{ backgroundColor: "var(--brand-blue)" }}>
                <Phone size={18} /> Call for Commercial Service
              </a>
            </div>
            <div className="order-1 overflow-hidden rounded-2xl border border-slate-200 lg:order-2">
              <img src="/assets/jobs/commercial-loading-dock.jpg" alt="Commercial loading dock overhead doors" className="h-64 w-full object-cover lg:h-full" />
            </div>
          </div>
        </section>

        {/* 24/7 BAND */}
        <section className="mb-16 rounded-2xl px-6 py-10 text-center" style={{ backgroundColor: "var(--navy)" }}>
          <h2 className="text-2xl font-bold text-white sm:text-3xl">Garage Door Trouble Doesn't Follow a Schedule.</h2>
          <p className="mt-2 text-slate-300">That's why we're available 24/7.</p>
          <p className="mx-auto mt-3 max-w-2xl text-slate-400">
            Whether you're dealing with a broken spring, damaged cable, door off its track, a failed opener, or another
            garage door problem, give us a call.
          </p>
          <a href={BUSINESS.phoneHref} className="mx-auto mt-6 flex w-fit items-center gap-2 rounded-xl px-6 py-3.5 text-lg font-semibold text-white" style={{ backgroundColor: "var(--brand-blue)" }}>
            <Phone size={20} /> {BUSINESS.phone}
          </a>
          <p className="mt-3 text-sm font-semibold" style={{ color: "var(--gold)" }}>24/7 Garage Door Service</p>
        </section>

        {/* GALLERY */}
        <section className="mb-16" data-testid="gallery-section">
          <SectionTitle eyebrow="OUR WORK" title="Our Garage Door Work" subtitle="Photographs from our installations and repairs, plus examples of the commercial overhead door systems we service." />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {GALLERY.map((g, i) => (
              <div key={i} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`photo-${i}`}>
                <div className="h-52 overflow-hidden">
                  <img src={g.img} alt={g.caption} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                </div>
                <div className="p-4">
                  <p className="text-[15px] font-semibold text-slate-900">{g.caption}</p>
                  <p className="mt-1.5 flex items-center gap-1 text-xs font-bold" style={{ color: "var(--brand-blue)" }}>
                    {g.tag} <ArrowRight size={13} />
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* REVIEWS */}
        <section className="mb-16" data-testid="reviews-section">
          <SectionTitle eyebrow="REVIEWS" title="What Our Customers Say" />
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-slate-200 bg-white p-8 text-center">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} size={20} style={{ color: "var(--gold)" }} fill="var(--gold)" />)}
            </div>
            <p className="max-w-xl text-[15px] leading-relaxed text-slate-500">
              Customer reviews will appear here. Had work done by us? We'd appreciate hearing how it went — call or email and let us know.
            </p>
          </div>
        </section>

        {/* SERVICE AREA */}
        <section className="mb-16" data-testid="service-area">
          <SectionTitle
            eyebrow="SERVICE AREA"
            title="Serving Regina and Surrounding Areas"
            subtitle="Regina Garage Door Services proudly serves homeowners and businesses throughout Regina and the nearby area. Not sure if we cover your location? Give us a call."
          />
          <div className="flex flex-wrap gap-3">
            {SERVICE_AREAS.map((a, i) => (
              <span
                key={a}
                data-testid={`area-${a}`}
                className="flex items-center gap-2 rounded-full border px-4 py-2.5 text-[15px] font-semibold"
                style={i === 0
                  ? { backgroundColor: "var(--brand-blue)", borderColor: "var(--brand-blue)", color: "#fff" }
                  : { backgroundColor: "#fff", borderColor: "#e2e8f0", color: "var(--brand-blue)" }}
              >
                <MapPin size={15} /> {a}
              </span>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <SectionTitle eyebrow="THE HONEST ANSWERS" title="Questions we get every day" />
          <div className="space-y-2.5">
            {FAQS.map((f, i) => <Faq key={i} item={f} testid={`faq-${i}`} />)}
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="mb-16 flex flex-col items-center gap-3 py-8 text-center" data-testid="final-cta">
          <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Need Garage Door Service? Give Us a Call.</h2>
          <p className="text-sm font-semibold text-slate-500">Residential • Commercial • Repair • Installation • 24/7 Emergency Service</p>
          <a href={BUSINESS.phoneHref} className="mt-1 text-3xl font-extrabold" style={{ color: "var(--brand-blue)" }}>{BUSINESS.phone}</a>
          <a href={BUSINESS.phoneHref} data-testid="final-call" className="mt-2 flex items-center gap-2 rounded-xl px-6 py-3.5 font-semibold text-white" style={{ backgroundColor: "var(--brand-blue)" }}>
            <Phone size={18} /> Call Now
          </a>
          <p className="mt-2 text-sm font-semibold text-slate-500">{BUSINESS.name}</p>
        </section>

        {/* CONTACT + REQUEST */}
        <section id="request" className="grid gap-8 lg:grid-cols-2" data-testid="contact-section">
          <div>
            <Eyebrow>CONTACT</Eyebrow>
            <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">{BUSINESS.name}</h2>
            <p className="mt-2 text-slate-500">{BUSINESS.phone} · {BUSINESS.email} · Available 24/7</p>
            <div className="mt-6 space-y-3">
              <a href={BUSINESS.phoneHref} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-sm">
                <Phone size={20} style={{ color: "var(--brand-blue)" }} />
                <span className="font-semibold text-slate-900">{BUSINESS.phone}</span>
              </a>
              <a href={BUSINESS.emailHref} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-sm">
                <MessageSquare size={20} style={{ color: "var(--brand-blue)" }} />
                <span className="font-semibold text-slate-900">{BUSINESS.email}</span>
              </a>
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
                <MapPin size={20} style={{ color: "var(--brand-blue)" }} />
                <span className="font-semibold text-slate-900">{BUSINESS.area}</span>
              </div>
            </div>
          </div>
          <div>
            <Eyebrow>REQUEST SERVICE</Eyebrow>
            <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Tell us what's happening with your door</h2>
            <p className="mt-2 text-slate-500">Send a few details and we'll get back to you. For urgent problems, calling is fastest.</p>
            <div className="mt-5">
              <RequestForm />
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
